import PropTypes from "prop-types";
import { useCallback, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { QUERY_STATUS } from "constants/constants";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_RECEIVED_TEMPLATE_ACTIONS,
  DELIVERY_NOTE_RECEIVED_TEMPLATE_DISMISS,
  DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES,
  DELIVERY_NOTE_TEMPLATE_ITEM_DETAIL,
} from "constants/forms";
import InfoContext from "pages/InfoContext";
import DeleteModal from "components/modal/DeleteModal";
import Icon from "core/icon/Icon";
import LoaderV2 from "core/loader/LoaderV2";
import { Typography } from "core/typography/Typography";
import { countColumnsByType } from "utils/deliveryNoteHelper";
import DeliveryNoteTemplatePreviewModal from "./DeliveryNoteTemplatePreviewModal";

const DeliveryNoteReceivedTemplateList = ({
  receivedTemplates,
  hasActiveFilters,
  loadStatus,
  isBusy,
  onImport,
  onDismiss,
}) => {
  const intl = useIntl();
  const { confirmAction } = useContext(InfoContext);
  const previewLabel = intl.formatMessage(
    DELIVERY_NOTE_RECEIVED_TEMPLATE_ACTIONS.preview
  );
  const dismissLabel = intl.formatMessage(
    DELIVERY_NOTE_RECEIVED_TEMPLATE_ACTIONS.dismiss
  );
  const importLabel = intl.formatMessage(
    DELIVERY_NOTE_RECEIVED_TEMPLATE_ACTIONS.import
  );

  const senderName = (received) =>
    received?.shared_by_name ||
    intl.formatMessage(DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.unknownSender);

  const [previewed, setPreviewed] = useState(null);
  const [dismissed, setDismissed] = useState(null);

  const closePreview = useCallback(() => setPreviewed(null), []);

  const handleImport = async (received) => {
    closePreview();
    confirmAction({ status: QUERY_STATUS.LOADING });
    const created = await onImport(received.share_id);
    confirmAction({
      status: created ? QUERY_STATUS.SUCCESS : QUERY_STATUS.ERROR,
      messages: {
        ...DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.templateImportReceived,
        values: { name: created?.name || received.name },
      },
    });
  };

  const confirmDismiss = async () => {
    const received = dismissed;
    setDismissed(null);
    confirmAction({ status: QUERY_STATUS.LOADING });
    const isDismissed = await onDismiss(received.share_id);
    confirmAction({
      status: isDismissed ? QUERY_STATUS.SUCCESS : QUERY_STATUS.ERROR,
      messages: {
        ...DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.templateDismiss,
        values: { name: received.name },
      },
    });
  };

  return (
    <section
      className="delivery-note-received-templates"
      id="delivery-note-received-templates"
    >
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            {...DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.title}
          />
        }
      />
      <Typography
        className="title-5 light-slate-gray"
        type="p"
        label={
          <FormattedMessage
            {...DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.subtitle}
          />
        }
      />

      {loadStatus === QUERY_STATUS.LOADING && (
        <LoaderV2 id="delivery-note-received-templates-loader" />
      )}

      {loadStatus === QUERY_STATUS.ERROR && (
        <Typography
          className="text-3 alizarin-crimson"
          type="p"
          label={
            <FormattedMessage
              {...DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.loadError}
            />
          }
        />
      )}

      {loadStatus === QUERY_STATUS.SUCCESS && !receivedTemplates?.length && (
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...(hasActiveFilters
                ? DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.noMatch
                : DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.empty)}
            />
          }
        />
      )}

      <ul className="delivery-note-received-templates__list">
        {(receivedTemplates || []).map((received) => {
          const counts = countColumnsByType(received.columns);
          return (
            <li
              className="delivery-note-received-templates__item"
              key={received.share_id}
            >
              <div className="delivery-note-received-templates__identity">
                <div className="delivery-note-received-templates__name">
                  <Typography
                    className="title-5 deep-purple"
                    type="span"
                    label={received.name}
                  />
                  <span
                    className="delivery-note-received-templates__badge"
                    data-testid={`delivery-note-received-template-badge-${received.share_id}`}
                  >
                    <FormattedMessage
                      {...DELIVERY_NOTE_RECEIVED_TEMPLATE_MESSAGES.sharedBy}
                      values={{ name: senderName(received) }}
                    />
                  </span>
                </div>
                <Typography
                  className="text-3 violet"
                  type="span"
                  label={
                    <FormattedMessage
                      {...DELIVERY_NOTE_TEMPLATE_ITEM_DETAIL}
                      values={{
                        issuer: received.issuer?.company_name || "—",
                        recipient: received.recipient?.company_name || "—",
                        simple: counts.simple,
                        linked: counts.linked,
                      }}
                    />
                  }
                />
              </div>
              <div className="delivery-note-received-templates__buttons">
                <button
                  type="button"
                  className="delivery-note-received-templates__action"
                  onClick={() => setPreviewed(received)}
                  aria-label={previewLabel}
                  title={previewLabel}
                  data-testid={`delivery-note-received-template-preview-${received.share_id}`}
                >
                  <Icon icon="eye" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-received-templates__action"
                  onClick={() => handleImport(received)}
                  disabled={isBusy}
                  aria-label={importLabel}
                  title={importLabel}
                  data-testid={`delivery-note-received-template-import-${received.share_id}`}
                >
                  <Icon icon="import" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-received-templates__action"
                  onClick={() => setDismissed(received)}
                  disabled={isBusy}
                  aria-label={dismissLabel}
                  title={dismissLabel}
                  data-testid={`delivery-note-received-template-dismiss-${received.share_id}`}
                >
                  <Icon icon="trash-2" color="red" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {previewed && (
        <DeliveryNoteTemplatePreviewModal
          template={previewed}
          onClose={closePreview}
          onImport={handleImport}
          isImporting={isBusy}
        />
      )}

      {dismissed && (
        <DeleteModal
          open
          onClose={() => setDismissed(null)}
          onValidate={confirmDismiss}
          validationMessages={
            DELIVERY_NOTE_RECEIVED_TEMPLATE_DISMISS.validationMessages
          }
          values={{ name: senderName(dismissed) }}
          highlight={dismissed.name}
          confirmLabel={DELIVERY_NOTE_RECEIVED_TEMPLATE_DISMISS.confirmLabel}
        />
      )}
    </section>
  );
};

DeliveryNoteReceivedTemplateList.propTypes = {
  receivedTemplates: PropTypes.array,
  hasActiveFilters: PropTypes.bool,
  loadStatus: PropTypes.string,
  isBusy: PropTypes.bool,
  onImport: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default DeliveryNoteReceivedTemplateList;
